/**
 * The terrain painter (`projects/terrain-tile-art.md` Phase 3, `decisions/0184`): paints one chunk
 * of the board onto a Canvas2D context from the pure geometry (`geometry.ts`), the art spec
 * (`tileArt.ts`) and the scene (`scene.ts`). Ported from the Phase 1 prototype's `art.js`.
 *
 * Code draws every shape (merged, feathered regions, props, walls, rims, shadows); the atlas
 * supplies only the surface inside them. The light is fixed in the north-west, baked into the
 * atlas, so every code-drawn shadow falls south-east.
 *
 * A chunk paints its cells plus a halo of {@link CHUNK_HALO} cells and clips to its own cells, so
 * props, shadows and rims that cross a chunk edge land in both chunks the same. Canvas shadow
 * offsets and blur are in device pixels, not user space, so every one goes through `shadow()`,
 * which scales by the resolution. No `ctx.filter`: Safari does not support it everywhere.
 */
import { CELL } from "../combatCells";
import { wallNodes } from "../combatWalls";
import { type CellRect, type Field, type Point, CHUNK_HALO, STEP, contours, fbm, fieldAt, growRect, gridCrosses, maskCells, maskHas, regionField, rnd, softProps, wallSegments } from "./geometry";
import { type Ctx2D, type Materials, pattern } from "./materials";
import { type PreparedScene, type PropDraw, type SceneRegion, containerBoxes, coverProps, propPose, wallHeight } from "./scene";
import { type ImpassableLayer, type MaterialId, GROUNDS, TERRACES } from "./tileArt";

/**
 * The "can't walk here" rim on impassable ground: a dark, see-through edge with a faint shade just
 * inside it, so it reads as a solid edge on light and dark ground alike. Cream and white rings are
 * the board's selection, hover and targeting marks, so the rim stays dark (developer, 2026-09-28: a
 * lit rim round a stump read as selected).
 */
const RIM = "rgba(10,8,5,0.55)";
const RIM_SHADE = { color: "rgba(0,0,0,0.22)", width: 12 } as const;
/** An edge wall's lit cap line and post caps. */
const WALL_CAP = "#efe2bf";

/** The grid crosses' ink on every ground: the GMS//OS bone (`--dir-bone`), neutral rather than tinted per ground. */
const CROSS_INK = "rgba(233,230,223,0.62)";

/** Where a chunk paints: its cells, and the device pixels per board pixel. */
export type ChunkTarget = { ctx: Ctx2D; rect: CellRect; resolution: number };

/** The canvas size in device pixels for a chunk. */
export function chunkPixelSize(rect: CellRect, resolution: number): { width: number; height: number } {
  return { width: Math.ceil((rect.x1 - rect.x0) * CELL * resolution), height: Math.ceil((rect.y1 - rect.y0) * CELL * resolution) };
}

type Painter = {
  ctx: Ctx2D;
  rect: CellRect;
  /** The rect plus the halo: what the chunk considers, then clips away. */
  wide: CellRect;
  haloPx: number;
  res: number;
  materials: Materials;
  /** A blank layer the size of the chunk with the same board transform. */
  layer: () => Ctx2D;
  /** Draw a layer back onto `ctx` (or another layer). */
  put: (target: Ctx2D, layer: Ctx2D, alpha?: number) => void;
  shadow: (g: Ctx2D, s: { color: string; blur: number; dx?: number; dy?: number }) => void;
};

function makePainter(target: ChunkTarget, materials: Materials): Painter {
  const { ctx, rect, resolution: res } = target;
  const { width, height } = chunkPixelSize(rect, res);
  const ox = rect.x0 * CELL;
  const oy = rect.y0 * CELL;
  const toBoard = (g: Ctx2D) => g.setTransform(res, 0, 0, res, -ox * res, -oy * res);
  return {
    ctx,
    rect,
    wide: growRect(rect, CHUNK_HALO),
    haloPx: CHUNK_HALO * CELL,
    res,
    materials,
    layer() {
      const g = new OffscreenCanvas(width, height).getContext("2d")!;
      toBoard(g);
      return g;
    },
    put(to, layer, alpha = 1) {
      to.save();
      to.setTransform(1, 0, 0, 1, 0, 0);
      to.globalAlpha = alpha;
      to.drawImage(layer.canvas, 0, 0);
      to.restore();
    },
    shadow(g, { color, blur, dx = 0, dy = 0 }) {
      g.shadowColor = color;
      g.shadowBlur = blur * res;
      g.shadowOffsetX = dx * res;
      g.shadowOffsetY = dy * res;
    },
  };
}

/** Paint chunk `target.rect` of `scene`. The context's canvas must be `chunkPixelSize` big. */
export function paintChunk(target: ChunkTarget, scene: PreparedScene, materials: Materials): void {
  const p = makePainter(target, materials);
  const { ctx, rect } = p;
  ctx.save();
  ctx.setTransform(p.res, 0, 0, p.res, -rect.x0 * CELL * p.res, -rect.y0 * CELL * p.res);
  ctx.beginPath();
  ctx.rect(rect.x0 * CELL, rect.y0 * CELL, (rect.x1 - rect.x0) * CELL, (rect.y1 - rect.y0) * CELL);
  ctx.clip();

  paintGround(p, scene);
  const outlines = new Map<string, Path2D>();
  for (const region of scene.regions) paintSurface(p, region, outlines);
  for (const region of scene.regions) paintDetail(p, region);
  for (const region of scene.regions) if (region.layers.veil) paintVeil(p, region);
  const holes = new Path2D();
  for (const region of scene.regions) {
    const wall = region.layers.impassable;
    if (wall?.form === "sunken") holes.addPath(paintSunken(p, region, wall));
  }
  for (const region of scene.regions) {
    const wall = region.layers.impassable;
    if (wall?.form === "raised") holes.addPath(paintRaised(p, region, wall));
  }
  paintWalls(p, scene);
  for (const prop of coverProps(scene, p.wide)) paintProp(p, prop);
  for (const region of scene.regions) if (region.layers.hazard) paintHazardRim(p, region, outlines);
  if (scene.grid) paintGrid(p, scene, holes);
  ctx.restore();
}

// ── Region fields, outlines and masks ─────────────────────────────────────────────────────────────

function field(p: Painter, region: SceneRegion, params: { sigma: number; amp: number; scale: number }): Field {
  return regionField(region.mask, { rect: p.rect, haloPx: p.haloPx }, { ...params, feather: 0, seed: region.seed % 100000 });
}

/** Closed loops as a smooth path: quadratic curves through the edge midpoints. */
export function loopsPath(loops: Point[][]): Path2D {
  const path = new Path2D();
  for (const L of loops) {
    const n = L.length;
    const mid = (k: number): Point => [(L[k % n]![0] + L[(k + 1) % n]![0]) / 2, (L[k % n]![1] + L[(k + 1) % n]![1]) / 2];
    const m0 = mid(0);
    path.moveTo(m0[0], m0[1]);
    for (let k = 1; k <= n; k++) {
      const q = L[k % n]!;
      const m = mid(k);
      path.quadraticCurveTo(q[0], q[1], m[0], m[1]);
    }
    path.closePath();
  }
  return path;
}

/**
 * A region's feathered alpha as a layer: the field's 0.5 level at half opacity, fading over
 * `feather` either side. The field's samples become pixels centred on their lattice points and
 * are scaled up smoothly, so neighbouring chunks interpolate the same samples.
 */
function alphaLayer(p: Painter, F: Field, feather: number): Ctx2D {
  const small = new OffscreenCanvas(Math.max(1, F.nx), Math.max(1, F.ny));
  const sg = small.getContext("2d")!;
  const img = sg.createImageData(small.width, small.height);
  const f = Math.max(feather, 0.01);
  for (let k = 0; k < F.v.length; k++) {
    const t = Math.min(1, Math.max(0, (F.v[k]! - (0.5 - f)) / (2 * f)));
    img.data[k * 4 + 3] = 255 * t * t * (3 - 2 * t);
  }
  sg.putImageData(img, 0, 0);
  const g = p.layer();
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = "high";
  g.drawImage(small, F.i0 * STEP - STEP / 2, F.j0 * STEP - STEP / 2, F.nx * STEP, F.ny * STEP);
  return g;
}

/** Fill a pattern through an alpha layer onto the chunk. */
function fillThrough(p: Painter, alpha: Ctx2D, { fill, opacity = 1 }: { fill: (g: Ctx2D) => void; opacity?: number }): void {
  const g = p.layer();
  fill(g);
  g.globalCompositeOperation = "destination-in";
  p.put(g, alpha);
  p.put(p.ctx, g, opacity);
}

function fillPattern(p: Painter, id: MaterialId, opts: { ox?: number; oy?: number; rot?: number } = {}): (g: Ctx2D) => void {
  return (g) => {
    g.fillStyle = pattern(g, p.materials, { id, ...opts });
    const w = p.wide;
    g.fillRect(w.x0 * CELL, w.y0 * CELL, (w.x1 - w.x0) * CELL, (w.y1 - w.y0) * CELL);
  };
}

/** A shape's cast shadow alone: the shape is drawn far off the canvas and only its shadow lands. */
function castShadow(p: Painter, path: Path2D, s: { color: string; blur: number; dx: number; dy: number }): void {
  const FAR = 100000;
  const g = p.ctx;
  g.save();
  p.shadow(g, { color: s.color, blur: s.blur, dx: s.dx + FAR, dy: s.dy });
  g.translate(-FAR, 0);
  g.fillStyle = "#000";
  g.fill(path);
  g.restore();
}

// ── Ground ────────────────────────────────────────────────────────────────────────────────────────

/** The map ground, with its second surface blended in at low frequency so tiling never shows. */
function paintGround(p: Painter, scene: PreparedScene): void {
  const { ctx } = p;
  const ground = GROUNDS[scene.ground];
  fillPattern(p, ground.base)(ctx);
  // The blend mask sits on a half-cell lattice; a one-cell margin keeps the smooth upscale the
  // same on both sides of a chunk edge.
  const x0 = (p.rect.x0 - 1) * 2;
  const y0 = (p.rect.y0 - 1) * 2;
  const nx = (p.rect.x1 - p.rect.x0 + 2) * 2;
  const ny = (p.rect.y1 - p.rect.y0 + 2) * 2;
  const small = new OffscreenCanvas(nx, ny);
  const sg = small.getContext("2d")!;
  const img = sg.createImageData(nx, ny);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) img.data[(j * nx + i) * 4 + 3] = 255 * Math.min(1, Math.max(0, (fbm((x0 + i) / 9, (y0 + j) / 9, { seed: 4, octaves: 4 }) - 0.45) * 4));
  }
  sg.putImageData(img, 0, 0);
  const alpha = p.layer();
  alpha.imageSmoothingEnabled = true;
  alpha.drawImage(small, x0 * (CELL / 2), y0 * (CELL / 2), nx * (CELL / 2), ny * (CELL / 2));
  fillThrough(p, alpha, { fill: fillPattern(p, ground.alt, { ox: 37, oy: 11 }) });
  // A soft light falloff across the whole board for depth, in board coordinates.
  const bw = scene.board.width * CELL;
  const bh = scene.board.height * CELL;
  const sky = ctx.createLinearGradient(0, 0, bw, bh);
  sky.addColorStop(0, "rgba(255,240,210,0.06)");
  sky.addColorStop(1, "rgba(0,0,0,0.12)");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, bw, bh);
}

// ── Surfaces, details, veil ─────────────────────────────────────────────────────────────────────

function paintSurface(p: Painter, region: SceneRegion, outlines: Map<string, Path2D>): void {
  const s = region.layers.surface;
  if (!s) return;
  const F = field(p, region, s.region);
  const alpha = alphaLayer(p, F, s.region.feather);
  const seed = region.seed % 97;
  fillThrough(p, alpha, { fill: fillPattern(p, s.material, { ox: seed * 13, oy: seed * 7 }), opacity: s.alpha });
  const outline = loopsPath(contours(F, 0.5));
  outlines.set(region.key, outline);
  // A rough or liquid surface gets a dark lip inside its edge, so the edge reads as a change of
  // ground rather than a stain. A soft shadowed stroke stands in for the prototype's blur filter.
  if (s.rough || s.region.feather < 0.05) {
    const { ctx } = p;
    ctx.save();
    ctx.clip(outline);
    p.shadow(ctx, { color: "rgba(10,8,5,0.6)", blur: 3 });
    ctx.strokeStyle = "rgba(10,8,5,0.4)";
    ctx.lineWidth = 4;
    ctx.stroke(outline);
    ctx.restore();
  }
}

/** The outline a region's flush marks and hazard rim follow: its surface's, or a rough one. */
function outlineOf(p: Painter, region: SceneRegion, outlines: Map<string, Path2D>): Path2D {
  let o = outlines.get(region.key);
  if (!o) {
    o = loopsPath(contours(field(p, region, DETAIL_OUTLINE), 0.5));
    outlines.set(region.key, o);
  }
  return o;
}

/** Flush marks: drawn on the ground with no cast shadow, so they never read as cover. */
function paintDetail(p: Painter, region: SceneRegion): void {
  const d = region.layers.detail;
  if (!d) return;
  const { ctx } = p;
  const cells = maskCells(region.mask, p.wide);
  const salt = region.seed % 100000;
  if (d.kind === "mine") {
    for (const s of softProps(region.mask, p.wide, { perCell: 2, salt: salt + 51 })) mine(ctx, s.cx, s.cy);
    return;
  }
  // Whole marks only (4.10): a mark is drawn only where every point of it lies well inside the
  // region's outline, shrunk toward its own centre until it does, and dropped if it never does. The
  // outline softens the ground wash; it never cuts a root or a twig. The test reads the same field
  // the outline is contoured from, over a window wide enough for every mark of `p.wide`, so a chunk
  // keeps exactly the marks the whole board would.
  const F = regionField(region.mask, { rect: p.rect, haloPx: p.haloPx + CELL }, { ...DETAIL_OUTLINE, feather: 0, seed: salt });
  const inside = (pts: Point[]) => pts.every(([x, y]) => fieldAt(F, x, y) >= DETAIL_INSIDE);
  ctx.save();
  ctx.lineCap = "round";
  const fill = d.material ? pattern(ctx, p.materials, { id: d.material, rot: 20 }) : "#4a3a2c";
  for (const [x, y] of cells) {
    // `curve`: a cubic through four points; `poly`: a closed flat shard; otherwise a straight line.
    const marks: { pts: Point[]; curve?: boolean; poly?: boolean; width: number; ink?: string }[] = [];
    if (d.kind === "cables") {
      // Loose cable across the deck: three long, thin S curves per cell, black rubber with a sheen.
      for (let k = 0; k < 3; k++) {
        const a = rnd(x, y, salt + k * 7) * Math.PI * 2;
        const cx = (x + 0.5) * CELL + (rnd(x, y, salt + k * 7 + 3) - 0.5) * 12;
        const cy = (y + 0.5) * CELL + (rnd(x, y, salt + k * 7 + 4) - 0.5) * 12;
        const dx = Math.cos(a) * CELL * 0.55;
        const dy = Math.sin(a) * CELL * 0.55;
        const bend = (rnd(x, y, salt + k * 7 + 1) - 0.5) * CELL * 0.9;
        marks.push({
          pts: [
            [cx - dx, cy - dy],
            [cx - dx / 3 - dy * 0.3 + bend, cy - dy / 3 + dx * 0.3],
            [cx + dx / 3 + dy * 0.3 - bend, cy + dy / 3 - dx * 0.3],
            [cx + dx, cy + dy],
          ],
          curve: true,
          width: 2 + rnd(x, y, salt + k * 7 + 2),
          ink: ["#1b1d20", "#2a2e33", "#3a2a1c"][k]!,
        });
      }
    } else if (d.kind === "fragments") {
      // Scattered metal shards, flush with the deck: small irregular quads.
      for (let k = 0; k < 4; k++) {
        const cx = x * CELL + 6 + rnd(x, y, salt + k * 6) * (CELL - 12);
        const cy = y * CELL + 6 + rnd(x, y, salt + k * 6 + 1) * (CELL - 12);
        const r = 3 + 3.5 * rnd(x, y, salt + k * 6 + 2);
        const a0 = rnd(x, y, salt + k * 6 + 3) * Math.PI * 2;
        const pts: Point[] = [0, 1, 2, 3].map((j) => {
          const a = a0 + (j / 4) * Math.PI * 2 + (rnd(x, y, salt + k * 6 + 10 + j) - 0.5) * 0.9;
          const rr = r * (0.55 + 0.6 * rnd(x, y, salt + k * 6 + 20 + j));
          return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
        });
        marks.push({ pts, poly: true, width: 0.8 });
      }
    } else if (d.kind === "roots") {
      // Two roots crossing the cell edge to edge, each a gentle S curve.
      for (let k = 0; k < 2; k++) {
        const a = rnd(x, y, salt + k * 5) * Math.PI * 2;
        const cx = (x + 0.5) * CELL;
        const cy = (y + 0.5) * CELL;
        const dx = Math.cos(a) * CELL * 0.62;
        const dy = Math.sin(a) * CELL * 0.62;
        const bend = (rnd(x, y, salt + k * 5 + 1) - 0.5) * CELL * 0.5;
        const pts: Point[] = [
          [cx - dx, cy - dy],
          [cx - dx / 3 - dy * 0.02 + bend, cy - dy / 3 + bend],
          [cx + dx / 3 - bend, cy + dy / 3 - bend],
          [cx + dx, cy + dy],
        ];
        marks.push({ pts, curve: true, width: 4 + 2.5 * rnd(x, y, salt + k * 5 + 2) });
      }
    } else {
      // Broken branches: short twigs lying in every direction.
      for (let k = 0; k < 5; k++) {
        const cx = x * CELL + 4 + rnd(x, y, salt + k * 4) * (CELL - 8);
        const cy = y * CELL + 4 + rnd(x, y, salt + k * 4 + 1) * (CELL - 8);
        const a = rnd(x, y, salt + k * 4 + 2) * Math.PI;
        const len = 7 + 7 * rnd(x, y, salt + k * 4 + 3);
        const pts: Point[] = [
          [cx - (Math.cos(a) * len) / 2, cy - (Math.sin(a) * len) / 2],
          [cx + (Math.cos(a) * len) / 2, cy + (Math.sin(a) * len) / 2],
        ];
        marks.push({ pts, curve: false, width: 1.6 + rnd(x, y, salt + k) });
      }
    }
    for (const m of marks) {
      const pts = wholeMark(m.pts, { curve: m.curve === true, inside });
      if (!pts) continue;
      const path = new Path2D();
      path.moveTo(...pts[0]!);
      if (m.curve) path.bezierCurveTo(...pts[1]!, ...pts[2]!, ...pts[3]!);
      else for (const q of pts.slice(1)) path.lineTo(...q);
      if (m.poly) {
        path.closePath();
        ctx.fillStyle = fill;
        ctx.fill(path);
        ctx.strokeStyle = "rgba(210,215,220,0.45)";
        ctx.lineWidth = m.width;
        ctx.stroke(path);
        continue;
      }
      ctx.strokeStyle = "rgba(18,12,8,0.75)";
      ctx.lineWidth = m.width + 2;
      ctx.stroke(path);
      ctx.strokeStyle = m.ink ?? fill;
      ctx.lineWidth = m.width;
      ctx.stroke(path);
      if (m.ink) {
        // A cable's sheen along its top.
        ctx.strokeStyle = "rgba(220,225,230,0.18)";
        ctx.lineWidth = m.width * 0.35;
        ctx.stroke(path);
      }
    }
  }
  ctx.restore();
}

/** The detail outline's field parameters — `outlineOf`'s — and how far inside it a mark must lie. */
const DETAIL_OUTLINE = { sigma: 10, amp: 0.12, scale: 50 } as const;
const DETAIL_INSIDE = 0.62;

/**
 * A mark's control points, shrunk toward their centroid in steps until `inside` holds for the points
 * the mark passes through (a curve's samples, a line's ends), or `null` if it never does.
 */
function wholeMark(pts: Point[], { curve, inside }: { curve: boolean; inside: (pts: Point[]) => boolean }): Point[] | null {
  const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  for (const k of [1, 0.72, 0.5]) {
    const q = pts.map(([x, y]): Point => [cx + (x - cx) * k, cy + (y - cy) * k]);
    if (inside(curve ? bezierSamples(q) : q)) return q;
  }
  return null;
}

function bezierSamples([a, b, c, d]: Point[]): Point[] {
  const out: Point[] = [];
  for (let t = 0; t <= 1.0001; t += 0.25) {
    const u = 1 - t;
    out.push([u * u * u * a![0] + 3 * u * u * t * b![0] + 3 * u * t * t * c![0] + t * t * t * d![0], u * u * u * a![1] + 3 * u * u * t * b![1] + 3 * u * t * t * c![1] + t * t * t * d![1]]);
  }
  return out;
}

function mine(ctx: Ctx2D, cx: number, cy: number): void {
  // A disturbed-soil ring and a pressure plate, flush with the ground.
  ctx.save();
  ctx.translate(cx, cy);
  ctx.fillStyle = "rgba(30,22,15,0.55)";
  ctx.beginPath();
  ctx.arc(0, 0, 6.5, 0, Math.PI * 2);
  ctx.fill();
  const g = ctx.createRadialGradient(-1, -1, 0, 0, 0, 4.2);
  g.addColorStop(0, "#8a8c86");
  g.addColorStop(1, "#3a3c3a");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, 4.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(10,10,10,0.8)";
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.fillStyle = "#ff4a2a";
  ctx.beginPath();
  ctx.arc(0, 0, 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Walkable but blocks sight: a dark haze with drifting light streaks. */
function paintVeil(p: Painter, region: SceneRegion): void {
  const F = field(p, region, { sigma: 13, amp: 0.18, scale: 40 });
  const alpha = alphaLayer(p, F, 0.15);
  fillThrough(p, alpha, {
    fill: (g) => {
      const w = p.wide;
      g.fillStyle = "rgba(14,16,20,0.55)";
      g.fillRect(w.x0 * CELL, w.y0 * CELL, (w.x1 - w.x0) * CELL, (w.y1 - w.y0) * CELL);
      g.strokeStyle = "rgba(200,205,210,0.16)";
      g.lineWidth = 3;
      g.lineCap = "round";
      for (const [x, y] of maskCells(region.mask, w)) {
        const sx = x * CELL + rnd(x, y, 3) * CELL;
        const sy = y * CELL + rnd(x, y, 4) * CELL;
        g.beginPath();
        g.moveTo(sx - 12, sy + 4);
        g.quadraticCurveTo(sx, sy - 6, sx + 12, sy + 2);
        g.stroke();
      }
    },
  });
}

// ── Impassable ground ─────────────────────────────────────────────────────────────────────────────

/** A soft shadow cast inward from an outline's edge, for a lit or shaded lip. */
function innerEdge(p: Painter, outline: Path2D, s: { color: string; dx: number; dy: number; blur: number }): void {
  const { ctx } = p;
  ctx.save();
  ctx.clip(outline);
  const frame = new Path2D();
  const w = growRect(p.wide, 2);
  frame.rect(w.x0 * CELL, w.y0 * CELL, (w.x1 - w.x0) * CELL, (w.y1 - w.y0) * CELL);
  frame.addPath(outline);
  p.shadow(ctx, s);
  ctx.fillStyle = "#000";
  ctx.fill(frame, "evenodd");
  ctx.restore();
}

function rimStroke(p: Painter, outline: Path2D, width: number): void {
  const { ctx } = p;
  ctx.save();
  ctx.clip(outline);
  ctx.strokeStyle = RIM_SHADE.color;
  ctx.lineWidth = RIM_SHADE.width;
  ctx.stroke(outline);
  ctx.restore();
  ctx.lineJoin = "round";
  ctx.strokeStyle = RIM;
  ctx.lineWidth = width;
  ctx.stroke(outline);
}

/**
 * A flight-blocking top: a second dark line a few pixels inside the edge, `lift` px up (a raised
 * cap). Drawn on its own layer so the gap between the two lines is cut out, not painted over.
 */
function doubleRim(p: Painter, outline: Path2D, lift = 0): void {
  const g = p.layer();
  g.translate(0, -lift);
  g.clip(outline);
  g.lineJoin = "round";
  g.strokeStyle = "#000";
  g.lineWidth = 13;
  g.stroke(outline);
  g.globalCompositeOperation = "destination-out";
  g.lineWidth = 8;
  g.stroke(outline);
  p.put(p.ctx, g, 0.55);
}

/** See-through impassable ground: a pit whose terraces step down inside the rim. */
function paintSunken(p: Painter, region: SceneRegion, wall: Extract<ImpassableLayer, { form: "sunken" }>): Path2D {
  const { ctx } = p;
  const outline = loopsPath(contours(field(p, region, wall.region), 0.5));
  ctx.fillStyle = pattern(ctx, p.materials, { id: wall.ledge });
  ctx.fill(outline);
  // Each band darker than the one above it, its lip lit. Bands come from a wider blur, so they sit
  // a third of a cell to a cell in from the rim.
  const deep = field(p, region, { sigma: TERRACES.sigma, amp: 0, scale: 1 });
  const alphas = [0.35, 0.45, 0.6];
  TERRACES.levels.forEach((t, k) => {
    const band = loopsPath(contours(deep, t));
    ctx.save();
    p.shadow(ctx, { color: "rgba(0,0,0,0.75)", blur: 6, dx: 4, dy: 5 });
    ctx.fillStyle = `rgba(6,5,4,${alphas[k]})`;
    ctx.fill(band);
    ctx.restore();
    ctx.strokeStyle = "rgba(236,214,160,0.28)";
    ctx.lineWidth = 1;
    ctx.stroke(band);
  });
  innerEdge(p, outline, { color: "rgba(0,0,0,0.9)", dx: 9, dy: 12, blur: 8 });
  if (region.layers.rim === "double") doubleRim(p, outline);
  rimStroke(p, outline, 2.5);
  return outline;
}

function cellsPath(cells: [number, number][]): Path2D {
  const path = new Path2D();
  for (const [x, y] of cells) path.rect(x * CELL, y * CELL, CELL, CELL);
  return path;
}

/**
 * Sight-blocking impassable ground: an extruded block seen from above and a little south. The
 * cap is lifted by the wall height, so the face shows along the footprint's south edges, and the
 * block casts a shadow south-east. The whole region stands at one height (`wallHeight`), so a run
 * of cells is one mass: one continuous cap, board-anchored, and the face and outline only on the
 * run's outer edge (4.9). Cap and face stay inside the footprint, which keeps "these cells are
 * blocked" exact.
 */
function paintRaised(p: Painter, region: SceneRegion, wall: Extract<ImpassableLayer, { form: "raised" }>): Path2D {
  const { ctx } = p;
  const outline = loopsPath(contours(field(p, region, wall.region), 0.5));
  const height = wallHeight({ range: wall.height, seed: region.seed % 1000 });
  castShadow(p, cellsPath(maskCells(region.mask, p.wide)), { color: "rgba(5,4,3,0.72)", blur: height * 0.7, dx: height * 0.8, dy: height * 1.1 });
  ctx.fillStyle = pattern(ctx, p.materials, { id: wall.face });
  ctx.fill(outline);
  ctx.save();
  ctx.clip(outline);
  ctx.fillStyle = "rgba(0,0,0,0.28)";
  ctx.fill(outline);
  ctx.translate(0, -height);
  ctx.save();
  p.shadow(ctx, { color: "rgba(0,0,0,0.65)", blur: 4, dy: 3 });
  // The cap's pattern is anchored to the board, not to the lifted block, so it runs on unbroken.
  ctx.fillStyle = pattern(ctx, p.materials, { id: wall.cap, rot: 35, oy: height });
  ctx.fill(outline);
  ctx.restore();
  if (wall.capMarks) capMarks(p, region, wall.capMarks);
  innerEdge(p, outline, { color: "rgba(255,244,214,0.3)", dx: 3, dy: 3, blur: 2 });
  innerEdge(p, outline, { color: "rgba(0,0,0,0.45)", dx: -3, dy: -3, blur: 3 });
  ctx.strokeStyle = "rgba(14,10,6,0.45)";
  ctx.lineWidth = 1.5;
  ctx.stroke(outline);
  if (region.layers.rim === "double") doubleRim(p, outline, height);
  ctx.restore();
  rimStroke(p, outline, region.layers.rim === "double" ? 3.5 : 3);
  return outline;
}

function capMarks(p: Painter, region: SceneRegion, marks: "seams" | "rings"): void {
  const { ctx } = p;
  if (marks === "seams") {
    // Plate seams on the cell lines.
    ctx.strokeStyle = "rgba(20,22,26,0.5)";
    ctx.lineWidth = 1.2;
    const path = new Path2D();
    for (const [x, y] of maskCells(region.mask, p.wide)) path.rect(x * CELL, y * CELL, CELL, CELL);
    ctx.stroke(path);
    return;
  }
  // Growth rings centred on each 2×2 block of the lattice the trunk mostly fills.
  ctx.strokeStyle = "rgba(60,40,24,0.45)";
  ctx.lineWidth = 1.3;
  const w = p.wide;
  for (let by = Math.floor(w.y0 / 2) * 2; by < w.y1; by += 2) {
    for (let bx = Math.floor(w.x0 / 2) * 2; bx < w.x1; bx += 2) {
      const n = [0, 1, 2, 3].filter((k) => maskHas(region.mask, bx + (k & 1), by + (k >> 1))).length;
      if (n < 3) continue;
      const cx = (bx + 1) * CELL + (rnd(bx, by, 41) - 0.5) * 10;
      const cy = (by + 1) * CELL + (rnd(bx, by, 42) - 0.5) * 10;
      for (let r = 6; r < CELL * 1.2; r += 5 + 3 * rnd(bx, by, r)) {
        ctx.beginPath();
        ctx.ellipse(cx, cy, r * (1 + 0.08 * rnd(bx, by, r + 1)), r * 0.92, rnd(bx, by, 43) * Math.PI, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }
}

// ── Edge walls ──────────────────────────────────────────────────────────────────────────────────

/**
 * Edge walls: a full wall is a raised line with a lit cap and a cast
 * shadow; the low walls are lower and keep the board's dash grammar for their cover grade
 * (dotted no cover, dashed soft, solid hard).
 */
function paintWalls(p: Painter, scene: PreparedScene): void {
  const { ctx } = p;
  const segs = wallSegments(scene.edges, p.wide);
  if (segs.length === 0) return;
  ctx.save();
  ctx.lineCap = "square";
  for (const s of segs) {
    const full = s.visual === "full";
    const line = new Path2D();
    line.moveTo(s.x1, s.y1);
    line.lineTo(s.x2, s.y2);
    ctx.setLineDash(s.visual === "low" ? [1.5, 5] : s.visual === "low-soft" ? [7, 5] : []);
    ctx.lineCap = s.visual === "low" ? "round" : "square";
    ctx.save();
    p.shadow(ctx, { color: "rgba(5,4,3,0.6)", blur: full ? 5 : 3, dx: full ? 5 : 2, dy: full ? 7 : 3 });
    ctx.strokeStyle = "#1a1712";
    ctx.lineWidth = full ? 6 : 4;
    ctx.stroke(line);
    ctx.restore();
    ctx.strokeStyle = full ? WALL_CAP : "rgba(239,226,191,0.8)";
    ctx.lineWidth = full ? 2.5 : 1.5;
    ctx.stroke(line);
  }
  // Corners and junctions get a post: a dark footing with a lit cap, so a turn reads as built.
  ctx.setLineDash([]);
  const w = p.wide;
  for (const n of wallNodes(scene.edges)) {
    if (n.kind === "terminal" || n.x < w.x0 || n.x > w.x1 || n.y < w.y0 || n.y > w.y1) continue;
    ctx.save();
    p.shadow(ctx, { color: "rgba(5,4,3,0.6)", blur: 4, dx: 3, dy: 4 });
    ctx.fillStyle = "#1a1712";
    ctx.beginPath();
    ctx.arc(n.x * CELL, n.y * CELL, 4.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = WALL_CAP;
    ctx.beginPath();
    ctx.arc(n.x * CELL, n.y * CELL, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// ── Cover props ─────────────────────────────────────────────────────────────────────────────────

function blobPoly(r: number, seed: number, { n = 9, squash = 0.82, jag = 0.28 }: { n?: number; squash?: number; jag?: number } = {}): Path2D {
  const path = new Path2D();
  const rot = rnd(seed, 3) * Math.PI;
  for (let k = 0; k < n; k++) {
    const a = rot + (k / n) * Math.PI * 2;
    const rr = r * (1 - jag / 2 + jag * rnd(seed, k + 11));
    const x = Math.cos(a) * rr;
    const y = Math.sin(a) * rr * squash;
    if (k) path.lineTo(x, y);
    else path.moveTo(x, y);
  }
  path.closePath();
  return path;
}

function rotated(path: Path2D, rot: number): Path2D {
  const out = new Path2D();
  out.addPath(path, new DOMMatrix().rotate((rot * 180) / Math.PI));
  return out;
}

type PropStyle = { path: Path2D; fill: string | CanvasPattern; outline?: string; lit?: number; shade?: number };

/**
 * One prop at its own centre: the cast shadow (short for soft cover, long for hard), the fill,
 * a lit-to-shaded gradient across it, and an outline.
 */
function drawProp(p: Painter, d: PropDraw, s: PropStyle): void {
  const { ctx } = p;
  const long = d.shadow === "long";
  ctx.save();
  ctx.translate(d.cx, d.cy);
  ctx.save();
  p.shadow(ctx, { color: "rgba(8,6,4,0.6)", blur: long ? 7 : 2, dx: long ? d.r * 0.55 : 1.5, dy: long ? d.r * 0.75 : 2 });
  ctx.fillStyle = s.fill;
  ctx.fill(s.path);
  ctx.restore();
  const g = ctx.createLinearGradient(-d.r, -d.r, d.r, d.r);
  g.addColorStop(0, `rgba(255,248,230,${s.lit ?? 0.3})`);
  g.addColorStop(0.5, "rgba(0,0,0,0)");
  g.addColorStop(1, `rgba(0,0,0,${s.shade ?? 0.45})`);
  ctx.fillStyle = g;
  ctx.fill(s.path);
  ctx.lineWidth = 1;
  ctx.strokeStyle = s.outline ?? "rgba(15,12,8,0.7)";
  ctx.stroke(s.path);
  ctx.restore();
}

/** A prop's fill, anchored to the board so neighbouring props don't repeat the same texels. */
function propFill(p: Painter, d: PropDraw, rot = 0): CanvasPattern {
  return pattern(p.ctx, p.materials, { id: d.material, ox: -d.cx, oy: -d.cy, rot });
}

/**
 * Props draw from a sprite cache: each prop is quantized (radius to half a pixel, rotation to a
 * sixteenth of a turn, shape to one of six variants), drawn once with its shadow into a small
 * canvas, then copied. Thousands of shadowed path fills cost seconds on a software canvas; the
 * copies cost milliseconds. The quantized prop is a pure function of the plan, so chunks agree.
 */
const SPRITES = new WeakMap<Materials, Map<string, { image: OffscreenCanvas; half: number }>>();

function paintProp(p: Painter, d: PropDraw): void {
  const pose = propPose(d);
  const q: PropDraw = { ...d, cx: 0, cy: 0, r: pose.r, rot: pose.rot, seed: pose.variant, scale: 1 };
  const key = `${q.kind}|${q.material}|${q.shadow}|${q.cluster}|${q.r}|${q.rot.toFixed(3)}|${q.seed}|${p.res}`;
  let cache = SPRITES.get(p.materials);
  if (!cache) SPRITES.set(p.materials, (cache = new Map()));
  let sprite = cache.get(key);
  if (!sprite) {
    // Room for the widest prop (two crossed logs) and a long shadow falling south-east.
    const half = Math.ceil(Math.max(q.r * 1.7, q.cluster === "single" ? 0 : CELL * 0.95) + 16);
    const image = new OffscreenCanvas(Math.ceil(2 * half * p.res), Math.ceil(2 * half * p.res));
    const g = image.getContext("2d")!;
    g.setTransform(p.res, 0, 0, p.res, half * p.res, half * p.res);
    drawPropShape({ ...p, ctx: g }, q);
    sprite = { image, half };
    cache.set(key, sprite);
  }
  // A prop shrunk to clear a collision (4.11) draws its sprite smaller about its centre.
  const half = sprite.half * d.scale;
  p.ctx.drawImage(sprite.image, d.cx - half, d.cy - half, 2 * half, 2 * half);
}

function drawPropShape(p: Painter, d: PropDraw): void {
  switch (d.kind) {
    case "shrub":
      return shrub(p, d);
    case "pebble":
      return drawProp(p, d, { path: blobPoly(d.r, d.seed, { n: 7, jag: 0.35 }), fill: propFill(p, d), lit: 0.35, shade: 0.4 });
    case "boulder": {
      const squash = d.cluster === "slab" ? 0.8 : d.cluster === "pair" ? 0.58 : 0.86;
      const path = blobPoly(d.r, d.seed, { n: 11, jag: 0.3, squash });
      return drawProp(p, d, { path: d.cluster === "pair" ? rotated(path, d.rot) : path, fill: propFill(p, d, rnd(d.seed, 9) * 90), lit: 0.34, shade: 0.55, outline: "rgba(10,9,7,0.85)" });
    }
    case "hull-plate":
      return plate(p, d);
    case "dead-stalk":
      return deadStalk(p, d);
    case "trunk":
      return trunk(p, d);
    case "log":
      return logs(p, d);
    case "crate":
      return crate(p, d);
    case "container":
      return containers(p, d);
    case "spool":
      return spool(p, d);
  }
}

/** A small supply crate: a square box with a lid cross, short shadow (soft cover, ruling 9). */
function crate(p: Painter, d: PropDraw): void {
  const s = d.r * 1.7;
  const path = rotated(rectPath(s, s), d.rot);
  drawProp(p, d, { path, fill: propFill(p, d, 90), outline: "rgba(14,12,10,0.85)", lit: 0.35, shade: 0.45 });
  const { ctx } = p;
  ctx.save();
  ctx.translate(d.cx, d.cy);
  ctx.rotate(d.rot);
  ctx.strokeStyle = "rgba(20,18,16,0.55)";
  ctx.lineWidth = 0.8;
  ctx.strokeRect(-s / 2 + 1.5, -s / 2 + 1.5, s - 3, s - 3);
  ctx.beginPath();
  ctx.moveTo(-s / 2 + 1.5, -s / 2 + 1.5);
  ctx.lineTo(s / 2 - 1.5, s / 2 - 1.5);
  ctx.moveTo(s / 2 - 1.5, -s / 2 + 1.5);
  ctx.lineTo(-s / 2 + 1.5, s / 2 - 1.5);
  ctx.stroke();
  ctx.restore();
}

/** Shipping containers (hard cover, ruling 9): corrugated boxes square to the deck, long shadows. */
function containers(p: Painter, d: PropDraw): void {
  const tint = ["#7a3b2a", "#2f5a6e", "#6b6a3a", "#4a4f57"][Math.floor(rnd(d.seed, 5) * 4)]!;
  for (const b of containerBoxes(d.cluster, { rot: d.rot, variant: d.seed })) {
    const q: PropDraw = { ...d, cx: d.cx + b.dx, cy: d.cy + b.dy };
    const path = rotated(rectPath(b.len, b.wid), b.rot);
    drawProp(p, q, { path, fill: propFill(p, q, (b.rot * 180) / Math.PI), outline: "rgba(8,9,10,0.9)", lit: 0.3, shade: 0.55 });
    const { ctx } = p;
    ctx.save();
    ctx.translate(q.cx, q.cy);
    ctx.rotate(b.rot);
    // The container's paint over the bare metal, then its corrugation across the width.
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = tint;
    ctx.fillRect(-b.len / 2 + 1, -b.wid / 2 + 1, b.len - 2, b.wid - 2);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "rgba(10,10,12,0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = -b.len / 2 + 4; x < b.len / 2 - 2; x += 3.2) {
      ctx.moveTo(x, -b.wid / 2 + 2);
      ctx.lineTo(x, b.wid / 2 - 2);
    }
    ctx.stroke();
    ctx.strokeStyle = "rgba(230,230,225,0.35)";
    ctx.strokeRect(-b.len / 2 + 2, -b.wid / 2 + 2, b.len - 4, b.wid - 4);
    ctx.restore();
  }
}

/** A cable spool: a round drum with its hub (soft cover over a cable snarl). */
function spool(p: Painter, d: PropDraw): void {
  const path = new Path2D();
  path.arc(0, 0, d.r, 0, Math.PI * 2);
  drawProp(p, d, { path, fill: propFill(p, d), outline: "rgba(10,10,12,0.85)", lit: 0.35, shade: 0.45 });
  const { ctx } = p;
  ctx.save();
  ctx.translate(d.cx, d.cy);
  ctx.strokeStyle = "rgba(24,26,30,0.8)";
  ctx.lineWidth = 1.2;
  for (const f of [0.72, 0.5]) {
    ctx.beginPath();
    ctx.arc(0, 0, d.r * f, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = "rgba(200,205,210,0.7)";
  ctx.beginPath();
  ctx.arc(0, 0, d.r * 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** An axis-aligned `w` × `h` rectangle centred on the origin. */
function rectPath(w: number, h: number): Path2D {
  const path = new Path2D();
  path.rect(-w / 2, -h / 2, w, h);
  return path;
}

function shrub(p: Painter, d: PropDraw): void {
  const path = new Path2D();
  const lobes = 3 + Math.floor(rnd(d.seed, 1) * 3);
  for (let k = 0; k < lobes; k++) {
    const a = (k / lobes) * Math.PI * 2 + rnd(d.seed, 2) * 3;
    const off = d.r * 0.45;
    const lr = d.r * (0.55 + 0.25 * rnd(d.seed, k + 5));
    path.moveTo(Math.cos(a) * off + lr, Math.sin(a) * off);
    path.arc(Math.cos(a) * off, Math.sin(a) * off, lr, 0, Math.PI * 2);
  }
  drawProp(p, d, { path, fill: propFill(p, d), outline: "rgba(22,34,12,0.75)", lit: 0.32, shade: 0.4 });
}

/** Standing dead vegetation: a tuft of dry stems radiating from a dark base. */
function deadStalk(p: Painter, d: PropDraw): void {
  const path = new Path2D();
  const n = 6 + Math.floor(rnd(d.seed, 1) * 3);
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2 + rnd(d.seed, k + 3) * 0.7;
    const len = d.r * (0.9 + 0.6 * rnd(d.seed, k + 20));
    const w = 0.9;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    // A thin tapered blade as a closed shape, so it can carry a fill and a shadow.
    path.moveTo(-sin * w, cos * w);
    path.lineTo(cos * len, sin * len);
    path.lineTo(sin * w, -cos * w);
    path.closePath();
  }
  path.moveTo(d.r * 0.35, 0);
  path.arc(0, 0, d.r * 0.35, 0, Math.PI * 2);
  drawProp(p, d, { path, fill: propFill(p, d), outline: "rgba(30,24,18,0.8)", lit: 0.3, shade: 0.35 });
}

function plate(p: Painter, d: PropDraw): void {
  const size = d.cluster === "slab" ? 22 : d.cluster === "pair" ? 16 : d.r * 0.85;
  const long = d.cluster === "slab" ? 1.4 : d.cluster === "pair" ? 1.9 : 1;
  const w = size * (1.7 + 0.4 * rnd(d.seed, 1)) * long;
  const h = size * (0.95 + 0.35 * rnd(d.seed, 2));
  const rot = (d.cluster === "pair" ? d.rot : 0) + (rnd(d.seed, 3) - 0.5) * (long > 1 ? 0.5 : 1.6);
  // A bent hull plate: a quad with one clipped corner.
  const path = new Path2D();
  const pts: Point[] = [
    [-w / 2, -h / 2],
    [w / 2 - h * 0.35, -h / 2],
    [w / 2, -h / 2 + h * 0.35],
    [w / 2, h / 2],
    [-w / 2, h / 2],
  ];
  pts.forEach(([x, y], k) => (k ? path.lineTo(x, y) : path.moveTo(x, y)));
  path.closePath();
  const shape = rotated(path, rot);
  drawProp(p, d, { path: shape, fill: propFill(p, d), lit: 0.3, shade: 0.55, outline: "rgba(8,9,10,0.9)" });
  const { ctx } = p;
  ctx.save();
  ctx.translate(d.cx, d.cy);
  ctx.rotate(rot);
  ctx.strokeStyle = "rgba(20,22,24,0.7)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-w / 2 + w * 0.38, -h / 2);
  ctx.lineTo(-w / 2 + w * 0.38, h / 2);
  ctx.stroke();
  ctx.fillStyle = "rgba(210,215,220,0.55)";
  for (const [x, y] of [
    [-w / 2 + 3, -h / 2 + 3],
    [-w / 2 + 3, h / 2 - 3],
    [-w / 2 + w * 0.38 + 3, -h / 2 + 3],
    [-w / 2 + w * 0.38 + 3, h / 2 - 3],
  ] as Point[]) {
    ctx.beginPath();
    ctx.arc(x, y, 0.9, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** A dead tree seen from above: a broken trunk top with branch stubs, casting a long shadow. */
function trunk(p: Painter, d: PropDraw): void {
  const path = blobPoly(d.r * 0.78, d.seed, { n: 10, jag: 0.22, squash: 0.95 });
  const stubs = 3 + Math.floor(rnd(d.seed, 5) * 2);
  for (let k = 0; k < stubs; k++) {
    const a = (k / stubs) * Math.PI * 2 + rnd(d.seed, k + 30) * 1.2;
    const len = d.r * (1.2 + 0.45 * rnd(d.seed, k + 40));
    const w = d.r * 0.17;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    path.moveTo(-sin * w + cos * d.r * 0.4, cos * w + sin * d.r * 0.4);
    path.lineTo(cos * len, sin * len);
    path.lineTo(sin * w + cos * d.r * 0.4, -cos * w + sin * d.r * 0.4);
    path.closePath();
  }
  drawProp(p, d, { path, fill: propFill(p, d, 90), outline: "rgba(18,12,8,0.85)", lit: 0.3, shade: 0.5 });
  // The broken top: a paler core with a few rings.
  const { ctx } = p;
  ctx.save();
  ctx.translate(d.cx, d.cy);
  ctx.fillStyle = pattern(ctx, p.materials, { id: "bark-cap", ox: -d.cx, oy: -d.cy });
  const core = blobPoly(d.r * 0.5, d.seed + 7, { n: 8, jag: 0.3, squash: 0.95 });
  ctx.fill(core);
  ctx.strokeStyle = "rgba(60,40,24,0.55)";
  ctx.lineWidth = 0.9;
  for (const f of [0.34, 0.2]) {
    ctx.beginPath();
    ctx.arc(0, 0, d.r * f, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Fallen logs: one along a pair, two crossed over a 2×2, a short one over a single cell. */
function logs(p: Painter, d: PropDraw): void {
  const one = ({ rot, len, w, salt }: { rot: number; len: number; w: number; salt: number }) => {
    const path = new Path2D();
    const c = Math.cos(rot);
    const s = Math.sin(rot);
    const hx = (c * len) / 2;
    const hy = (s * len) / 2;
    path.moveTo(-hx - s * w, -hy + c * w);
    path.lineTo(hx - s * w, hy + c * w);
    path.arc(hx, hy, w, rot + Math.PI / 2, rot - Math.PI / 2, true);
    path.lineTo(-hx + s * w, -hy - c * w);
    path.arc(-hx, -hy, w, rot - Math.PI / 2, rot + Math.PI / 2, true);
    path.closePath();
    drawProp(p, { ...d, seed: d.seed + salt }, { path, fill: propFill(p, d, (rot * 180) / Math.PI), outline: "rgba(18,12,8,0.85)", lit: 0.28, shade: 0.5 });
    // A sawn or broken end showing the rings.
    const { ctx } = p;
    ctx.save();
    ctx.translate(d.cx + hx, d.cy + hy);
    ctx.fillStyle = pattern(ctx, p.materials, { id: "bark-cap", ox: -d.cx, oy: -d.cy });
    ctx.beginPath();
    ctx.ellipse(0, 0, w * 0.45, w * 0.95, rot, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(60,40,24,0.6)";
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.restore();
  };
  if (d.cluster === "slab") {
    const a = rnd(d.seed, 1) * Math.PI;
    one({ rot: a, len: CELL * 1.7, w: 8.5, salt: 0 });
    one({ rot: a + 1.1 + rnd(d.seed, 2) * 0.6, len: CELL * 1.5, w: 7.5, salt: 1 });
  } else if (d.cluster === "pair") one({ rot: d.rot, len: CELL * 1.75, w: d.r * 0.4, salt: 0 });
  else one({ rot: d.rot, len: CELL * 0.95, w: d.r * 0.45, salt: 0 });
}

// ── Hazard rims and grid ────────────────────────────────────────────────────────────────────────

function paintHazardRim(p: Painter, region: SceneRegion, outlines: Map<string, Path2D>): void {
  const color = region.layers.hazard!.color;
  const { ctx } = p;
  const o = outlineOf(p, region, outlines);
  ctx.save();
  ctx.clip(o);
  p.shadow(ctx, { color, blur: 14 });
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.45;
  ctx.lineWidth = 10;
  ctx.stroke(o);
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = "rgba(20,10,4,0.7)";
  ctx.lineWidth = 4;
  ctx.stroke(o);
  p.shadow(ctx, { color, blur: 10 });
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke(o);
  ctx.restore();
}

/** Cell-corner crosses, larger every fifth line, clipped out of impassable ground (ruling 2). */
function paintGrid(p: Painter, scene: PreparedScene, holes: Path2D): void {
  const { ctx } = p;
  ctx.save();
  const clip = new Path2D();
  const w = growRect(p.wide, 1);
  clip.rect(w.x0 * CELL, w.y0 * CELL, (w.x1 - w.x0) * CELL, (w.y1 - w.y0) * CELL);
  clip.addPath(holes);
  ctx.clip(clip, "evenodd");
  const cross = new Path2D();
  for (const c of gridCrosses(scene.board, p.wide)) {
    const a = c.major ? 6 : 3.5;
    cross.moveTo(c.x * CELL - a, c.y * CELL);
    cross.lineTo(c.x * CELL + a, c.y * CELL);
    cross.moveTo(c.x * CELL, c.y * CELL - a);
    cross.lineTo(c.x * CELL, c.y * CELL + a);
  }
  ctx.strokeStyle = "rgba(10,8,5,0.55)";
  ctx.lineWidth = 2.8;
  ctx.stroke(cross);
  ctx.strokeStyle = CROSS_INK;
  ctx.lineWidth = 1.1;
  ctx.stroke(cross);
  ctx.restore();
}
